<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Doughnut } from 'vue-chartjs';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { CHART_FONT_FAMILY } from '../../constants';

const props = defineProps({
  hitCount: {
    type: Number,
    default: 0,
  },
  missedCount: {
    type: Number,
    default: 0,
  },
  emptyMessage: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  missedLabelKey: {
    type: String,
    default: 'CUSTOM_SLA_REPORTS.CHARTS.SLA_MISSED',
  },
  metLabelKey: {
    type: String,
    default: 'CUSTOM_SLA_REPORTS.CHARTS.SLA_MET',
  },
});

ChartJS.register(ArcElement, Tooltip, Legend);

const { t } = useI18n();

const total = computed(() => props.hitCount + props.missedCount);
const hasData = computed(() => total.value > 0);

const percentageOf = count =>
  total.value ? Math.round((count / total.value) * 100) : 0;

const collection = computed(() => ({
  labels: [
    t(
      props.missedLabelKey,
      {
        count: props.missedCount,
        percentage: percentageOf(props.missedCount),
      },
      props.missedCount
    ),
    t(
      props.metLabelKey,
      {
        count: props.hitCount,
        percentage: percentageOf(props.hitCount),
      },
      props.hitCount
    ),
  ],
  datasets: [
    {
      backgroundColor: ['rgb(225, 29, 72)', 'rgb(5, 150, 105)'],
      data: [props.missedCount, props.hitCount],
    },
  ],
}));

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: { fontFamily: CHART_FONT_FAMILY, boxWidth: 12, padding: 12 },
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col gap-2 p-4 rounded-xl outline outline-1 outline-n-container bg-n-solid-2"
  >
    <span class="text-heading-3 text-n-slate-12">
      {{ title || $t('CUSTOM_SLA_REPORTS.CHARTS.OUTCOME_BREAKDOWN') }}
    </span>
    <div class="h-56">
      <Doughnut v-if="hasData" :data="collection" :options="chartOptions" />
      <div
        v-else
        class="flex items-center justify-center h-full text-sm text-n-slate-10"
      >
        {{ emptyMessage }}
      </div>
    </div>
  </div>
</template>
